import { useRef } from 'react';
import analyticsService from '@/services/analytics/analytics-service';
import * as Enums from '@/utils/enums';
import { FormHeader } from '@/interfaces/api/models';

/**
 * The forms analytics events (sent through analytics-service: PostHog, mirrored to Clarity).
 *
 * Forms are used heavily and PostHog bills per event, so an editor visit sends exactly one event:
 * `form_closed`, when it ends. It carries the shared form properties (see formProperties: which
 * editor, `v1` the original or `v2` the beta, which form and version, what it's attached to and its
 * status), how the visit ended (`outcome`), what opened it (`trigger`), how long it was open and
 * which features were used in it (the counters in FormFeatureCounts). Anything that happens inside
 * the editor is a property on that one event, never an event of its own.
 *
 * The only other events are rare actions outside the editor: printing from the forms list, and
 * hitting the copy limit or answering the unused-copies warning when duplicating.
 */

export const FORM_EVENTS = {
    closed: 'form_closed',
    /** Printed from the forms list (prints inside the editor are counted on `form_closed`). */
    printed: 'form_printed',
    duplicateLimitReached: 'form_duplicate_limit_reached',
    duplicateWarningAnswered: 'form_duplicate_warning_answered',
} as const;

export type FormEditor = 'v1' | 'v2';

/** What opened the editor: the list's row action or form picker, a duplicate, or Renew. */
export type FormOpenTrigger = 'start' | 'edit_draft' | 'view_completed' | 'add_other' | 'duplicate' | 'renew';

/** How an editor visit ended. */
export type FormCloseOutcome = 'draft_saved' | 'completed' | 'updated' | 'reverted' | 'renewed' | 'cancelled';

/** Feature use within one editor visit, sent on `form_closed`. */
export interface FormFeatureCounts {
    section_nav_clicks: number;
    repeat_sections_added: number;
    repeat_sections_removed: number;
    signatures_captured: number;
    attachments_uploaded: number;
    /** Complete (or Update on a completed form) pressed with validation errors. */
    complete_blocked: number;
    prints: number;
    /** "Update to Latest Version" applied. */
    upgrades: number;
}

const emptyCounts = (): FormFeatureCounts => ({
    section_nav_clicks: 0,
    repeat_sections_added: 0,
    repeat_sections_removed: 0,
    signatures_captured: 0,
    attachments_uploaded: 0,
    complete_blocked: 0,
    prints: 0,
    upgrades: 0,
});

/** "JobCard" → "job_card": enum names as event values. */
export const enumSlug = (name: string | null | undefined) =>
    name ? name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/\s+/g, '_').toLowerCase() : null;

const enumValue = (enumObject: any, value: number | null | undefined) =>
    value === null || value === undefined ? null : enumSlug(Enums.getEnumStringValue(enumObject, value));

const isPhone = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 48em)').matches;

/** The properties every event about one form carries. */
export const formProperties = (formHeader: FormHeader | null | undefined, editor: FormEditor | null = null) => {
    const definition = formHeader?.FormDefinition;
    const definitionName = definition?.Name ?? null;
    const displayName = formHeader?.FormDefinitionDisplayName || definitionName;
    return {
        editor,
        form_header_id: formHeader?.ID ?? null,
        form_definition_id: formHeader?.FormDefinitionID ?? null,
        form_master_id: definition?.MasterID ?? null,
        form_name: definitionName,
        form_version: definition?.Version ?? null,
        form_rule: enumValue(Enums.FormRule, definition?.FormRule),
        item_module: enumValue(Enums.Module, formHeader?.Module),
        form_status: formHeader?._isNew ? 'not_started' : enumValue(Enums.FormStatus, formHeader?.FormStatus),
        is_new: !!formHeader?._isNew,
        // A copy is named apart from its definition when it's duplicated (see confirmDuplicateName).
        is_copy: !!displayName && !!definitionName && displayName !== definitionName,
        is_multi_use: !!definition?.MultiUse,
        is_expiring: (definition?.ExpireTimespan ?? 0) > 0,
        is_phone: isPhone(),
    };
};

/** Records a form event with the shared form properties. Never throws. */
export const trackForm = (event: string, formHeader: FormHeader | null | undefined, editor: FormEditor | null, properties: Record<string, unknown> = {}) => {
    try {
        analyticsService.track(event, { ...formProperties(formHeader, editor), ...properties });
    } catch {
        // Analytics must never break the app.
    }
};

/**
 * One editor visit: counts feature use and sends the one `form_closed` when it ends. The header is
 * read when the visit ends, so a save or upgrade that replaced it is reflected.
 */
export const useFormSessionAnalytics = (editor: FormEditor, formHeader: FormHeader, isNew: boolean, trigger: FormOpenTrigger | null = null) => {
    const openedAt = useRef(Date.now());
    const counts = useRef<FormFeatureCounts>(emptyCounts());
    const autosaveRecovery = useRef<{ autosave_recovery: 'restored' | 'discarded' | null; autosave_draft_age_minutes: number | null }>({
        autosave_recovery: null,
        autosave_draft_age_minutes: null,
    });
    const ended = useRef(false);
    const header = useRef(formHeader);
    header.current = { ...formHeader, _isNew: isNew };

    const count = (feature: keyof FormFeatureCounts) => {
        counts.current[feature] += 1;
    };

    /** The v2 editor offered back unsaved changes from an earlier visit. */
    const setAutosaveRecovery = (choice: 'restored' | 'discarded', draftAgeMinutes: number | null) => {
        autosaveRecovery.current = { autosave_recovery: choice, autosave_draft_age_minutes: draftAgeMinutes };
    };

    /** Sends `form_closed` once; later calls are ignored. */
    const end = (outcome: FormCloseOutcome, properties: Record<string, unknown> = {}) => {
        if (ended.current) return;
        ended.current = true;
        trackForm(FORM_EVENTS.closed, header.current, editor, {
            outcome,
            trigger,
            seconds_open: Math.round((Date.now() - openedAt.current) / 1000),
            ...autosaveRecovery.current,
            ...counts.current,
            ...properties,
        });
    };

    return { count, setAutosaveRecovery, end };
};

/** Whole days since an ISO date, or null when there isn't one. */
export const daysSince = (isoDate: string | null | undefined): number | null => {
    if (!isoDate) return null;
    const time = new Date(isoDate).getTime();
    return Number.isNaN(time) ? null : Math.floor((Date.now() - time) / (24 * 60 * 60 * 1000));
};

/** Whole minutes since an ISO date, or null when there isn't one. */
export const minutesSince = (isoDate: string | null | undefined): number | null => {
    if (!isoDate) return null;
    const time = new Date(isoDate).getTime();
    return Number.isNaN(time) ? null : Math.round((Date.now() - time) / (60 * 1000));
};
